import { useState, useEffect } from 'react';
import { apiFetch } from '../App';
import Watermark from '../components/Watermark';

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway', pstbets_bw: 'PST Bets', sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267', bbets_bw: 'BBets', betxplosion_bw: 'BetXplosion',
};

const BOOKMAKER_COLORS = {
  betway_bw: '#00e5b0', pstbets_bw: '#3d8ef8', sunbet_bw: '#f5a623',
  bet267_bw: '#f04747', bbets_bw: '#12c98e', betxplosion_bw: '#a855f7',
};

const BOOKMAKER_URLS = {
  betway_bw: 'https://www.betway.co.bw',
  pstbets_bw: 'https://www.pstbet.co.bw',
  sunbet_bw: 'https://www.sunbet.co.bw',
  bet267_bw: 'https://www.bet267.co.bw',
  bbets_bw: 'https://www.bbets.co.bw',
  betxplosion_bw: 'https://www.betxplosion.co.bw',
};

function formatKickoff(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-BW', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function BestOddsSparkline({ snapshots, outcomeKey, color }) {
  // Build best price per timestamp across all bookmakers
  const timeMap = new Map();
  snapshots.forEach(s => {
    if (!s[outcomeKey] || !s.capturedAt) return;
    const t = new Date(s.capturedAt).getTime();
    const existing = timeMap.get(t);
    if (!existing || s[outcomeKey] > existing) timeMap.set(t, s[outcomeKey]);
  });

  const points = [...timeMap.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([t, price]) => ({ t, price }));

  if (points.length < 2) return (
    <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', padding: '12px 0' }}>
      Not enough data yet
    </div>
  );

  const prices = points.map(p => p.price);
  const min = Math.min(...prices) * 0.98;
  const max = Math.max(...prices) * 1.02;
  const W = 300, H = 60;

  const svgPoints = points.map((p, i) => {
    const x = (i / (points.length - 1)) * W;
    const y = H - ((p.price - min) / (max - min || 1)) * H;
    return `${x},${y}`;
  }).join(' ');

  const first = prices[0];
  const last = prices[prices.length - 1];
  const up = last > first;
  const changed = last !== first;

  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
        <polyline points={svgPoints} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <div style={{ fontSize: 11, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: 'var(--text3)' }}>Open: {first}</span>
        <span style={{ color: changed ? (up ? 'var(--success)' : 'var(--danger)') : 'var(--text3)' }}>
          {changed ? (up ? '↑' : '↓') : '→'} {last}
        </span>
      </div>
    </div>
  );
}

export default function MatchDetail({ item, onBack }) {
  const [bookmakerOdds, setBookmakerOdds] = useState([]);
  const [bestPrices, setBestPrices] = useState({});
  const [snapshots, setSnapshots] = useState([]);
  const [ouMarkets, setOuMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [width, setWidth] = useState(window.innerWidth);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [activeTab, setActiveTab] = useState('match');
  const [goalsSubTab, setGoalsSubTab] = useState(null);
  const [bestOddsData, setBestOddsData] = useState({});

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;
  const kickoff = item.startTime || item.commenceTime;

  useEffect(() => { loadData(); }, [item]);

  async function loadData() {
    setLoading(true);
    try {
      const [data, snapData] = await Promise.all([
        apiFetch(`/best-odds/match/${item._id}`),
        apiFetch(`/best-odds/snapshots/${item._id}`).catch(() => ({ snapshots: [] })),
      ]);
      setBookmakerOdds(data.bookmakers || []);
      setOuMarkets(data.ouMarkets || item.ouMarkets || []);
      setBookmakerOdds(data.bookmakers || []);
setOuMarkets(data.ouMarkets || item.ouMarkets || []);
setBestOddsData(data);
setBestPrices({
  home: data.bestHome?.price,
  draw: data.bestDraw?.price,
  away: data.bestAway?.price,
});
      setSnapshots(snapData.snapshots || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function loadAIAnalysis() {
    setAiLoading(true);
    setAiError(null);
    try {
      const data = await apiFetch('/match-analyst', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          normalizedMatchKey: item.normalizedMatchKey || item._id,
          homeTeam: item.homeTeam,
          awayTeam: item.awayTeam,
          league: item.league,
          bestHome: item.bestHome,
          bestDraw: item.bestDraw,
          bestAway: item.bestAway,
        }),
      });
      setAiAnalysis(data);
    } catch (err) {
      setAiError('Could not generate analysis. Please try again.');
    } finally {
      setAiLoading(false);
    }
  }

  const bestHomeBook = bookmakerOdds.find(b => b.home?.price === bestPrices.home);
  const bestDrawBook = bookmakerOdds.find(b => b.draw?.price === bestPrices.draw);
  const bestAwayBook = bookmakerOdds.find(b => b.away?.price === bestPrices.away);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Watermark />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <button className="btn btn-outline" style={{ fontSize: 12, padding: '6px 14px', flexShrink: 0 }} onClick={onBack}>
          ← Back
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: isMobile ? 15 : 22, fontWeight: 800 }}>
            {item.homeTeam} vs {item.awayTeam}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
            {item.league} · {formatKickoff(kickoff)}
          </div>
        </div>
        <span className={`pill ${item.status === 'ARB' ? 'pill-green' : item.status === 'NEAR_ARB' ? 'pill-amber' : 'pill-blue'}`}>
          {item.status === 'ARB' ? '⚡ ARB' : item.status === 'NEAR_ARB' ? 'Near Arb' : 'No Arb'}
        </span>
      </div>

      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3,1fr)', gap: 10 }}>
        <div className="stat-card" style={{ '--accent-color': 'var(--accent)' }}>
          <div className="stat-label">Book %</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 14 : 22 }}>{item.bookPercent?.toFixed(2)}%</div>
          <div className="stat-change neutral">Combined implied prob</div>
        </div>
        <div className="stat-card" style={{ '--accent-color': item.arbMargin > 0 ? 'var(--success)' : 'var(--danger)' }}>
          <div className="stat-label">Margin</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 14 : 22, color: item.arbMargin > 0 ? 'var(--success)' : 'var(--danger)' }}>
            {item.arbMargin > 0 ? '+' : ''}{item.arbMargin?.toFixed(2)}%
          </div>
          <div className={`stat-change ${item.arbMargin > 0 ? 'up' : 'down'}`}>
            {item.arbMargin > 0 ? 'Profit available' : 'No arb currently'}
          </div>
        </div>
        <div className="stat-card" style={{ '--accent-color': 'var(--accent2)' }}>
          <div className="stat-label">Bookmakers</div>
          <div className="stat-value" style={{ fontSize: isMobile ? 14 : 22 }}>{bookmakerOdds.length || '—'}</div>
          <div className="stat-change neutral">→ BW bookmakers</div>
        </div>
      </div>

      {/* Open bookmaker links */}
      <div className="card" style={{ padding: '12px 16px' }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: .5 }}>
          Open bookmakers
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[...new Set(bookmakerOdds.map(b => b.bookmaker))].map(bm => (
            <a key={bm} href={BOOKMAKER_URLS[bm]} target="_blank" rel="noopener noreferrer"
              className="btn btn-outline" style={{ fontSize: 11, padding: '5px 12px' }}>
              {BOOKMAKER_LABELS[bm]} ↗
            </a>
          ))}
        </div>
      </div>

{/* Market tabs */}
<div style={{ display: 'flex', gap: 6, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
  {['match', 'goals', 'other'].map(tab => (
    <button key={tab} onClick={() => setActiveTab(tab)} style={{
      background: 'none', border: 'none', cursor: 'pointer',
      padding: '8px 16px', fontSize: 13, fontWeight: activeTab === tab ? 700 : 400,
      color: activeTab === tab ? 'var(--accent)' : 'var(--text3)',
      borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
      marginBottom: -1, textTransform: 'capitalize',
    }}>
      {tab === 'match' ? '⚽ Match' : tab === 'goals' ? '🎯 Goals' : '📋 Other'}
    </button>
  ))}
</div>

      {activeTab === 'match' && (
  <>
    {/* Main grid */}
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="section-header">
          <div>
            <div className="section-title">📊 1X2 Comparison</div>
            <div className="section-sub">Best price highlighted ✓</div>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ minWidth: isMobile ? 300 : 'auto' }}>
            <thead>
              <tr>
                <th>Bookmaker</th>
                <th style={{ textAlign: 'center' }}>Home</th>
                <th style={{ textAlign: 'center' }}>Draw</th>
                <th style={{ textAlign: 'center' }}>Away</th>
                <th style={{ textAlign: 'right' }}>Margin</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5}><div className="skeleton" style={{ height: 40, margin: 8 }} /></td></tr>
              ) : bookmakerOdds.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: 'var(--text3)' }}>No odds data</td></tr>
              ) : bookmakerOdds.map(bm => (
                <tr key={bm.bookmaker}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: BOOKMAKER_COLORS[bm.bookmaker] || 'var(--accent)', flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 500 }}>{BOOKMAKER_LABELS[bm.bookmaker] || bm.bookmaker}</span>
                      {bm.doubtful && <span className="pill pill-amber" style={{ fontSize: 9 }} title="This bookmaker's match was paired with lower confidence">check match</span>}
                    </div>
                  </td>
                  <td style={{ textAlign: 'center' }}><PriceCell price={bm.home?.price} isBest={bm.home?.price === bestPrices.home} /></td>
                  <td style={{ textAlign: 'center' }}><PriceCell price={bm.draw?.price} isBest={bm.draw?.price === bestPrices.draw} /></td>
                  <td style={{ textAlign: 'center' }}><PriceCell price={bm.away?.price} isBest={bm.away?.price === bestPrices.away} /></td>
                  <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text3)' }}>
                    {bm.margin != null ? `${bm.margin > 0 ? '+' : ''}${bm.margin}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && bookmakerOdds.some(b => b.doubtful) && (
          <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--warning)' }}>
            ⚠ Some bookmakers were paired with lower confidence — check the team names on the bookmaker's site before betting.
          </div>
        )}
        {!loading && bookmakerOdds.length > 0 && (
          <div style={{ padding: '14px 16px', background: 'rgba(0,229,176,0.04)', borderTop: '1px solid var(--border)', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <BestCell label={`Home · ${BOOKMAKER_LABELS[bestHomeBook?.bookmaker] || '—'}`} price={bestPrices.home} />
            <BestCell label={`Draw · ${BOOKMAKER_LABELS[bestDrawBook?.bookmaker] || '—'}`} price={bestPrices.draw} />
            <BestCell label={`Away · ${BOOKMAKER_LABELS[bestAwayBook?.bookmaker] || '—'}`} price={bestPrices.away} />
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <ArbCalculator bestPrices={bestPrices} bestHomeBook={bestHomeBook} bestDrawBook={bestDrawBook} bestAwayBook={bestAwayBook} />
        <HowToBet bestPrices={bestPrices} bestHomeBook={bestHomeBook} bestDrawBook={bestDrawBook} bestAwayBook={bestAwayBook} />
      </div>
    </div>

    {snapshots.length > 0 && (
      <div className="card">
        <div className="section-header">
          <div>
            <div className="section-title">📈 Odds Movement</div>
            <div className="section-sub">Best price over time · 1 = Home, X = Draw, 2 = Away</div>
          </div>
        </div>
        <div style={{ padding: '0 16px 16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 16 }}>
            {[
              { label: `1 — ${item.homeTeam}`, key: 'home', color: '#00e5b0' },
              { label: 'X — Draw', key: 'draw', color: '#3d8ef8' },
              { label: `2 — ${item.awayTeam}`, key: 'away', color: '#f5a623' },
            ].map(leg => (
              <div key={leg.key}>
                <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8, color: 'var(--text2)' }}>{leg.label}</div>
                <BestOddsSparkline snapshots={snapshots} outcomeKey={leg.key} color={leg.color} />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(61,142,248,0.06)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: 'var(--text3)' }}>
            💡 Each line shows the best available price across all bookmakers at each scan cycle.
          </div>
        </div>
      </div>
    )}
  </>
)}

{activeTab === 'goals' && (
  <div className="card">
    <div className="section-header">
      <div className="section-title">🎯 Goals Markets</div>
    </div>
    <div style={{ padding: '0 16px 16px' }}>
      {/* Sub-tabs: one per O/U line + BTTS */}
      {(() => {
        const ouLines = [...new Set(bookmakerOdds.flatMap(bm => (bm.ouLines || []).map(l => l.handicap)))].sort((a, b) => a - b);
        const subTabs = [...ouLines.map(h => ({ key: String(h), label: `O/U ${h}` })), { key: 'btts', label: 'BTTS' }];
        const active = goalsSubTab || subTabs[0]?.key;
        return (
          <>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
              {subTabs.map(st => (
                <button key={st.key} onClick={() => setGoalsSubTab(st.key)} style={{
                  padding: '5px 14px', fontSize: 12, borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', cursor: 'pointer',
                  background: active === st.key ? 'var(--accent)' : 'var(--bg3)',
                  color: active === st.key ? '#000' : 'var(--text2)',
                  fontWeight: active === st.key ? 700 : 400,
                }}>
                  {st.label}
                </button>
              ))}
            </div>

            {active === 'btts' ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Bookmaker</th>
                    <th style={{ textAlign: 'center' }}>Yes</th>
                    <th style={{ textAlign: 'center' }}>No</th>
                  </tr>
                </thead>
                <tbody>
                  {bookmakerOdds.filter(bm => bm.bttsYes || bm.bttsNo).length === 0 ? (
                    <tr><td colSpan={3} style={{ textAlign: 'center', padding: 20, color: 'var(--text3)' }}>No BTTS data</td></tr>
                  ) : bookmakerOdds.filter(bm => bm.bttsYes || bm.bttsNo).map(bm => (
                    <tr key={bm.bookmaker}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: BOOKMAKER_COLORS[bm.bookmaker] || 'var(--accent)', flexShrink: 0 }} />
                          <span style={{ fontSize: 12, fontWeight: 500 }}>{BOOKMAKER_LABELS[bm.bookmaker] || bm.bookmaker}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <PriceCell price={bm.bttsYes} isBest={bm.bttsYes === bestOddsData.bestBttsYes?.price} />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <PriceCell price={bm.bttsNo} isBest={bm.bttsNo === bestOddsData.bestBttsNo?.price} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Bookmaker</th>
                    <th style={{ textAlign: 'center' }}>Over {active}</th>
                    <th style={{ textAlign: 'center' }}>Under {active}</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const h = parseFloat(active);
                    const bestOu = (ouMarkets || []).find(ou => ou.handicap === h);
                    const rows = bookmakerOdds.map(bm => {
                      const line = (bm.ouLines || []).find(l => l.handicap === h);
                      return line ? { bookmaker: bm.bookmaker, over: line.over, under: line.under } : null;
                    }).filter(Boolean);
                    if (!rows.length) return <tr><td colSpan={3} style={{ textAlign: 'center', padding: 20, color: 'var(--text3)' }}>No data for this line</td></tr>;
                    return rows.map(row => (
                      <tr key={row.bookmaker}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: BOOKMAKER_COLORS[row.bookmaker] || 'var(--accent)', flexShrink: 0 }} />
                            <span style={{ fontSize: 12, fontWeight: 500 }}>{BOOKMAKER_LABELS[row.bookmaker] || row.bookmaker}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <PriceCell price={row.over} isBest={row.over === bestOu?.over?.price} />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <PriceCell price={row.under} isBest={row.under === bestOu?.under?.price} />
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            )}
          </>
        );
      })()}
    </div>
  </div>
)}

{activeTab === 'other' && (
  <div className="card">
    <div className="section-header">
      <div className="section-title">📋 Other Markets</div>
    </div>
    <div style={{ padding: '0 16px 16px' }}>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>Double Chance</div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Bookmaker</th>
            <th style={{ textAlign: 'center' }}>1X</th>
            <th style={{ textAlign: 'center' }}>X2</th>
            <th style={{ textAlign: 'center' }}>12</th>
          </tr>
        </thead>
        <tbody>
          {bookmakerOdds.filter(bm => bm.dc1X || bm.dcX2 || bm.dc12).length === 0 ? (
            <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: 'var(--text3)' }}>No Double Chance data</td></tr>
          ) : bookmakerOdds.filter(bm => bm.dc1X || bm.dcX2 || bm.dc12).map(bm => (
            <tr key={bm.bookmaker}>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: BOOKMAKER_COLORS[bm.bookmaker] || 'var(--accent)', flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 500 }}>{BOOKMAKER_LABELS[bm.bookmaker] || bm.bookmaker}</span>
                </div>
              </td>
              <td style={{ textAlign: 'center' }}>
                <PriceCell price={bm.dc1X} isBest={bm.dc1X === bestOddsData.best1X?.price} />
              </td>
              <td style={{ textAlign: 'center' }}>
                <PriceCell price={bm.dcX2} isBest={bm.dcX2 === bestOddsData.bestX2?.price} />
              </td>
              <td style={{ textAlign: 'center' }}>
                <PriceCell price={bm.dc12} isBest={bm.dc12 === bestOddsData.best12?.price} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}

      {/* AI Match Analyst */}
      <div className="card">
        <div className="section-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div className="section-title">🤖 AI Match Analyst</div>
              <span className="pill pill-blue" style={{ fontSize: 9 }}>BETA</span>
            </div>
            <div className="section-sub">Powered by Groq AI</div>
          </div>
        </div>
        <div style={{ padding: '0 16px 16px' }}>
          {!aiAnalysis && !aiLoading && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>
                Get an AI-powered breakdown including value opportunities and betting recommendations.
              </div>
              <button className="btn btn-primary" onClick={loadAIAnalysis} style={{ fontSize: 13, padding: '10px 24px' }}>
                🤖 Analyse This Match
              </button>
            </div>
          )}
          {aiLoading && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div className="skeleton" style={{ height: 20, marginBottom: 8 }} />
              <div className="skeleton" style={{ height: 20, marginBottom: 8 }} />
              <div className="skeleton" style={{ height: 20 }} />
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 12 }}>Analysing...</div>
            </div>
          )}
          {aiError && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--danger)', fontSize: 13 }}>
              {aiError}
              <button className="btn btn-outline" style={{ marginLeft: 12, fontSize: 11 }} onClick={loadAIAnalysis}>Retry</button>
            </div>
          )}
          {aiAnalysis && !aiLoading && (
            <div>
              <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '14px 16px', fontSize: 13, lineHeight: 1.8, color: 'var(--text2)' }}>
                {aiAnalysis.analysis.split('\n').map((line, i) => {
                  const parts = line.split(/\*\*(.*?)\*\*/g);
                  return (
                    <div key={i} style={{ marginBottom: line.trim() === '' ? 8 : 4 }}>
                      {parts.map((part, j) =>
                        j % 2 === 1
                          ? <strong key={j} style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>{part}</strong>
                          : <span key={j}>{part}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                  Generated {aiAnalysis.generatedAt ? new Date(aiAnalysis.generatedAt).toLocaleTimeString() : ''}
                  {aiAnalysis.fromCache ? ' · cached' : ''}
                </span>
                <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={() => setAiAnalysis(null)}>↺ Regenerate</button>
              </div>
              <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(61,142,248,0.06)', borderRadius: 'var(--radius-sm)', fontSize: 11, color: 'var(--text3)' }}>
                ⚠️ AI analysis is for informational purposes only. Always verify odds with bookmakers before betting.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PriceCell({ price, isBest }) {
  if (!price) return <span style={{ color: 'var(--text3)' }}>—</span>;
  return (
    <span style={{
      fontFamily: 'var(--font-display)', fontWeight: isBest ? 700 : 400,
      fontSize: isBest ? 14 : 13,
      color: isBest ? 'var(--accent)' : 'var(--text2)',
      background: isBest ? 'rgba(0,229,176,0.1)' : 'transparent',
      padding: isBest ? '2px 6px' : '2px 0', borderRadius: 4,
      display: 'inline-block',
    }}>
      {price} {isBest && '✓'}
    </span>
  );
}

function BestCell({ label, price }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 18, color: 'var(--accent)' }}>{price ?? '—'}</div>
      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{label}</div>
    </div>
  );
}

function ArbCalculator({ bestPrices, bestHomeBook, bestDrawBook, bestAwayBook }) {
  const [stake, setStake] = useState(100);
  const h = bestPrices.home || 0;
  const d = bestPrices.draw || 0;
  const a = bestPrices.away || 0;
  const total = h && d && a ? (1/h) + (1/d) + (1/a) : h && a ? (1/h) + (1/a) : 1;
  const isArb = total < 1;

  const legs = d
    ? [
        { label: `Home · ${BOOKMAKER_LABELS[bestHomeBook?.bookmaker] || '—'}`, odds: h },
        { label: `Draw · ${BOOKMAKER_LABELS[bestDrawBook?.bookmaker] || '—'}`, odds: d },
        { label: `Away · ${BOOKMAKER_LABELS[bestAwayBook?.bookmaker] || '—'}`, odds: a },
      ]
    : [
        { label: `Home · ${BOOKMAKER_LABELS[bestHomeBook?.bookmaker] || '—'}`, odds: h },
        { label: `Away · ${BOOKMAKER_LABELS[bestAwayBook?.bookmaker] || '—'}`, odds: a },
      ];

  const stakes = legs.map(l => isArb ? ((stake * (1/l.odds)) / total).toFixed(2) : (stake / legs.length).toFixed(2));
  const returns = isArb ? (stakes[0] * legs[0].odds).toFixed(2) : '—';
  const profit = isArb ? (returns - stake).toFixed(2) : '—';

  return (
    <div className="card">
      <div className="section-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>⚡</span>
          <div className="section-title">Arb Calculator</div>
        </div>
      </div>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="input-group">
          <label className="input-label">Total stake (BWP)</label>
          <input type="number" className="input-field" value={stake} onChange={e => setStake(parseFloat(e.target.value) || 100)} />
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[100, 200, 500, 1000].map(a => (
            <button key={a} className="btn btn-ghost" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setStake(a)}>P{a}</button>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {legs.map((leg, i) => (
            <div key={leg.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 500 }}>{leg.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>@ {leg.odds}</div>
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent)', fontSize: 16 }}>P{stakes[i]}</div>
            </div>
          ))}
        </div>
        {isArb ? (
          <div style={{ background: 'rgba(18,201,142,0.08)', border: '1px solid rgba(18,201,142,0.2)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>Guaranteed return</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16 }}>P{returns}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: 'var(--text3)' }}>Guaranteed profit</div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: 'var(--success)' }}>+P{profit}</div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '8px 0' }}>
            No arb — book% is {(total * 100).toFixed(2)}%
          </div>
        )}
      </div>
    </div>
  );
}

function HowToBet({ bestPrices, bestHomeBook, bestDrawBook, bestAwayBook }) {
  const legs = [
    { outcome: 'Home', book: bestHomeBook, price: bestPrices.home },
    bestPrices.draw ? { outcome: 'Draw', book: bestDrawBook, price: bestPrices.draw } : null,
    { outcome: 'Away', book: bestAwayBook, price: bestPrices.away },
  ].filter(Boolean);

  return (
    <div className="card">
      <div className="section-header"><div className="section-title">🎯 Where to bet</div></div>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {legs.map(leg => (
          <div key={leg.outcome} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'var(--bg3)', borderRadius: 'var(--radius-sm)' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500 }}>{leg.outcome}</div>
              <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 2 }}>{BOOKMAKER_LABELS[leg.book?.bookmaker] || '—'}</div>
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16 }}>{leg.price ?? '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}